import type { ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getShadowAiCategoryLabel } from '@kirin/api-client/contracts/shadow-ai';
import type { ShadowAiToolListItem } from '@kirin/api-client/contracts/shadow-ai';
import { severityLabelMap } from '@kirin/api-client/contracts/shared/severity';
import {
  BrowserToolEvidence,
  type BrowserToolEvidenceData,
} from './BrowserToolEvidence';
import { formatDateTime } from '@/lib/date-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';

/**
 * The dialog needs only these fields. KIR-3495: the user-scope page opens it
 * from a DataTable row that carries no risk level or detection sources, so
 * both are optional — the `Row` helper below hides a field with no value
 * rather than the caller inventing one.
 */
export type BrowserHistoryDetailTool = Pick<
  ShadowAiToolListItem,
  'name' | 'toolId' | 'category' | 'addedAt' | 'lastUsed' | 'extras'
> &
  Partial<
    Pick<
      ShadowAiToolListItem,
      'riskLevel' | 'sources' | 'version' | 'userCount'
    >
  > & {
    /**
     * Pre-aggregated KIR-3890 evidence, supplied by callers whose row already
     * carries it as first-class fields (the browser-AI-tools DataTable). Takes
     * precedence over `extras`, which is raw per-user agent output: it is
     * absent on legacy rows and, when several machines report the same domain,
     * holds one machine's numbers rather than the row's totals.
     */
    evidence?: BrowserToolEvidenceData;
  };

/**
 * Fallback for callers that only have the agent's raw `extras` bag (the
 * personal Shadow AI tools table). Returns null when it carries no visit
 * count, so the panel is hidden rather than rendered as all-zeroes.
 */
function evidenceFromExtras(
  tool: BrowserHistoryDetailTool
): BrowserToolEvidenceData | null {
  const extras = tool.extras;
  if (extras?.visitCount == null) return null;
  return {
    domain: tool.toolId,
    visitCount: Number(extras.visitCount ?? 0) || 0,
    realVisitCount: Number(extras.realVisitCount ?? 0) || 0,
    navigationType: extras.navigationType ?? null,
    sampleMatchedUrl: extras.sampleMatchedUrl ?? null,
    firstDetected: tool.addedAt,
    lastSeen: tool.lastUsed,
    realCount: Number(extras.realCount ?? 0) || 0,
    redirectCount: Number(extras.redirectCount ?? 0) || 0,
    addressBarCount: Number(extras.addressBarCount ?? 0) || 0,
    adsTrackingCount: Number(extras.adsTrackingCount ?? 0) || 0,
  };
}

interface BrowserHistoryDetailDialogProps {
  tool: BrowserHistoryDetailTool | null;
  onClose: () => void;
}

function Row({
  label,
  value,
}: {
  label: string;
  value: string | null;
}): ReactElement | null {
  if (!value || !value.trim()) return null;
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="break-all">{value}</span>
    </div>
  );
}

/**
 * Row → detail panel for a Browser History entry (KIR-3813). Replaces the
 * old off-app `<a href>` domain link with an in-app detail view, matching the
 * AgentCommandEventDetailDialog pattern (shared Dialog; no Sheet primitive).
 */
export function BrowserHistoryDetailDialog({
  tool,
  onClose,
}: BrowserHistoryDetailDialogProps): ReactElement {
  const timeZone = useDisplayTimeZone();
  const evidence = tool ? (tool.evidence ?? evidenceFromExtras(tool)) : null;

  return (
    <Dialog open={!!tool} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="break-all">
            {tool?.name || tool?.toolId}
          </DialogTitle>
          <DialogDescription>
            Detected AI tool from browser history.
          </DialogDescription>
        </DialogHeader>

        {tool && (
          <div className="flex min-w-0 flex-col gap-2">
            <Row label="Domain" value={tool.toolId} />
            <Row
              label="Category"
              value={getShadowAiCategoryLabel(tool.category)}
            />
            <Row
              label="Risk"
              value={tool.riskLevel ? severityLabelMap[tool.riskLevel] : null}
            />
            <Row label="Version" value={tool.version ?? null} />
            <Row
              label="Users"
              value={
                tool.userCount === undefined ? null : String(tool.userCount)
              }
            />
            <Row
              label="Detection sources"
              value={tool.sources?.length ? tool.sources.join(', ') : null}
            />
            <Row
              label="First detected"
              value={
                tool.addedAt ? formatDateTime(tool.addedAt, timeZone) : null
              }
            />
            <Row
              label="Last seen"
              value={
                tool.lastUsed ? formatDateTime(tool.lastUsed, timeZone) : null
              }
            />

            {evidence && <BrowserToolEvidence evidence={evidence} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

BrowserHistoryDetailDialog.displayName = 'BrowserHistoryDetailDialog';
